import type {
  ExpensePart,
  UpdateExpensePart,
} from '@pflegenavi/frontend/api-nursing-home';
import { v4 as uuid } from 'uuid';

/**
 * Editor-side part. `_internalId` is a client-only stable identity used as
 * the React row key and for update/remove targeting. Server-loaded parts
 * mirror it from their server `id`; newly-added (unsaved) parts get a
 * `new-${uuid()}` value. Matches the receipt-batch `LineItem._internalId`
 * convention so the editor pattern stays consistent across modules.
 */
export interface PartDraft extends UpdateExpensePart {
  _internalId: string;
}

/** Client-only id assigned to newly-added (unsaved) parts. */
export function createInternalId(): string {
  return `new-${uuid()}`;
}

/**
 * Map server-side parts to editor drafts. `_internalId` mirrors the server
 * `id` so saved parts keep a stable key across reloads.
 */
export function expensePartsToDrafts(parts: ExpensePart[]): PartDraft[] {
  return parts.map((p) => ({
    _internalId: p.id,
    id: p.id,
    amount: p.amount,
    expense_subcategory_id: p.expense_subcategory?.id ?? null,
    expense_part_tax: p.expense_part_tax
      ? {
          country_id: p.expense_part_tax.country_id,
          tax_amount: p.expense_part_tax.tax_amount,
          tax_name: p.expense_part_tax.tax_name,
          tax_abbreviation: p.expense_part_tax.tax_abbreviation,
          explanation_short: p.expense_part_tax.explanation_short,
          explanation_long: p.expense_part_tax.explanation_long,
          display_explanation: p.expense_part_tax.display_explanation,
          paragraph: p.expense_part_tax.paragraph,
        }
      : null,
  }));
}

/**
 * Convert drafts to the API payload. `_internalId` is dropped (client-only).
 * For new parts, `id` is undefined so the backend treats them as creates.
 * For tax: omit when null so the backend doesn't try to cast null into the
 * assoc.
 */
export function draftsToUpdateParts(parts: PartDraft[]): UpdateExpensePart[] {
  return parts.map((p) => {
    const out: UpdateExpensePart = {
      amount: p.amount,
      expense_subcategory_id: p.expense_subcategory_id ?? null,
    };
    if (p.id) {
      out.id = p.id;
    }
    if (p.expense_part_tax) {
      out.expense_part_tax = p.expense_part_tax;
    }
    return out;
  });
}
